import {
  Configuration as OrgConfiguration,
  ErrorContext,
  Middleware,
  OrgControllerApi,
  OrgResearchControllerApi,
  SearchControllerApi,
  ServiceProviderControllerApi
} from '@/api/origin-org-svc'

import {
  UserControllerApi,
  Configuration as UserMgmtConfiguration,
  RequestContext,
  OorTitleControllerApi,
  UserRoleControllerApi,
  OnboardControllerApi,
  EmailControllerApi
} from '@/api/origin-user-mgmt-svc'
import constants from '@/config/constants'
import useStore from '../state/store'
import { CustomError } from './CustomException'
import {
  CommonWorkflowControllerApi,
  Configuration as WorkflowConfiguration,
  ECRPrerequisitesControllerApi,
  OORPrerequisitesControllerApi,
  UtilControllerApi as UtilWorkflowControllerApi,
  VLEIPrerequisitesControllerApi,
  WorkflowInstanceControllerApi
} from '@/api/origin-workflow-svc'
import {
  Configuration as L10nConfiguration,
  L10nControllerApi
} from '@/api/origin-l10n-svc'
import {
  Configuration as NotificationConfiguration,
  UINotificationControllerApi
} from '@/api/origin-notification-svc'
import {
  AidControllerApi,
  Configuration as AgentConfiguration,
  MultisigControllerApi,
  UtilControllerApi as UtilAgentControllerApi
} from '@/api/origin-agent-svc'
import { isUrlBlacklistedForSignedHeaders } from '@/utils/common'
import { CLIENT_CONNECTIVITY_STATUS } from '@/state/signify'
import {
  Configuration as WebmeetConfiguration,
  WebmeetControllerApi
} from '@/api/origin-webmeet-svc'

/**
 * Closure factory for `waitForSignifyConnection`.
 *
 * Accepts injected `getState` and `subscribe` functions so unit tests can
 * control store state without mocking the global Zustand store.
 *
 * The gate predicate requires ALL FOUR conditions:
 *   1. clientConnectivityStatus === CONNECTED
 *   2. signifyClient is non-null
 *   3. userAidHab is non-null
 *   4. signifyInterceptorAttached is true  ← Security Hawk critical #2 fix
 *
 * Condition 4 prevents a race where the store flips CONNECTED before
 * attachInterceptor has replaced window.fetch — without it, the first
 * signed API call would go out on the bare (unsigned) originalFetch.
 *
 * Note: `subscribe` is accepted for testability only. It MUST NOT be
 * exposed in ApiConfigOptions — doing so re-creates the auth-bypass leak
 * vector identified by Security Hawk concern #5.
 *
 * @param getState - called at invocation time (not construction time) to
 *   avoid the stale-snapshot bug. Narrow to the 5-field signify slice at
 *   the call site so the runtime surface matches the type annotation.
 * @param subscribe - Zustand subscribeWithSelector; injected for testability.
 */
export function makeWaitForReady(
  getState: () => Pick<
    import('@/state/store').IStore,
    | 'clientConnectivityStatus'
    | 'signifyClient'
    | 'userAidHab'
    | 'promptForPassCode'
    | 'showPassCodePrompt'
    | 'signifyInterceptorAttached'
  >,
  subscribe: typeof useStore.subscribe = useStore.subscribe
): () => Promise<void> {
  return () =>
    new Promise((resolve, reject) => {
      // Narrow to 5-field signify slice at invocation time (Security Hawk concern 5).
      const s = getState()
      const store = {
        clientConnectivityStatus: s.clientConnectivityStatus,
        signifyClient: s.signifyClient,
        userAidHab: s.userAidHab,
        promptForPassCode: s.promptForPassCode,
        showPassCodePrompt: s.showPassCodePrompt,
        signifyInterceptorAttached: s.signifyInterceptorAttached
      }

      if (
        store.clientConnectivityStatus ===
          CLIENT_CONNECTIVITY_STATUS.CONNECTED &&
        store.signifyClient &&
        store.userAidHab &&
        store.signifyInterceptorAttached
      ) {
        resolve()
        return
      }

      if (!store.promptForPassCode) {
        store.showPassCodePrompt()
      }

      // Use `let` (not `const`) so the unsubscribe reference is available
      // inside the callback before the assignment completes (avoids TDZ).
      // eslint-disable-next-line prefer-const
      let unsubscribe = subscribe(
        (state) => state.clientConnectivityStatus,
        (status) => {
          // Read live state inside the subscriber — getState() is called at
          // subscribe-callback time, not at factory construction time, so
          // signifyClient, userAidHab, and signifyInterceptorAttached are live.
          const live = getState()
          if (
            status === CLIENT_CONNECTIVITY_STATUS.CONNECTED &&
            live.signifyClient &&
            live.userAidHab &&
            live.signifyInterceptorAttached
          ) {
            unsubscribe()
            resolve()
          } else if (status === CLIENT_CONNECTIVITY_STATUS.ERROR) {
            unsubscribe()
            reject(new Error('Failed to connect to SignifyClient'))
          }
        }
      )
    })
}

export const authMiddleware: Middleware = {
  pre: async (context: RequestContext): Promise<void> => {
    // Ensure headers object exists in the init object
    if (!context.init.headers) {
      context.init.headers = new Headers()
    }

    const headers: Headers = new Headers(context.init.headers)
    headers.set('Content-Type', 'application/json')

    // Assign the modified headers back to the init object
    context.init.headers = headers
  },
  onError: async (context: ErrorContext): Promise<Response | void> => {
    throw context.error
  }
}

// Production waitForSignifyConnection — the factory is invoked lazily on
// first call so that `useStore.subscribe` and `useStore.getState` are read
// at call time, not at module-init time. Under signify-ts TLA the useStore
// module is async-loaded, and a module-init read of `useStore.subscribe`
// can hit TDZ in the webpack-generated chunk getter ("Cannot access 's'
// before initialization" — see PR #787 docker-build TDZ regression).
let _waitForSignifyConnection: (() => Promise<void>) | null = null
const waitForSignifyConnection: () => Promise<void> = () => {
  if (!_waitForSignifyConnection) {
    _waitForSignifyConnection = makeWaitForReady(
      () => useStore.getState(),
      useStore.subscribe
    )
  }
  return _waitForSignifyConnection()
}

async function handleErrorResponse(
  response: any,
  store: Pick<
    import('@/state/store').IStore,
    'killSession' | 'clearSignify'
  > = useStore.getState()
) {
  let error: CustomError

  if (response.status === 401) {
    console.error(`HTTP 401 Unauthorized, redirecting to signin page`)
    store.killSession()
    store.clearSignify()

    error = new CustomError({
      title: 'ERROR-401',
      type: '',
      instance: '',
      code: 'UNAUTHORIZED',
      detail: 'Unauthorized access detected.'
    })
  } else {
    const bodyError = await response.json()
    error = new CustomError({
      code: bodyError?.code || constants.ERROR_CODES.UNKNOWN,
      title: bodyError?.title || '',
      type: bodyError?.type || '',
      detail: bodyError?.detail || '',
      instance: bodyError?.instance || ''
    })
  }

  throw error
}

/**
 * Closure factory for the signed-request fetch adapter.
 *
 * Accepts an injectable `waitForReady` function so unit tests can inject a
 * no-op stub without mocking the global Zustand store.
 *
 * MUST NOT add Sentry capture inside this closure — signed request bodies
 * and signature headers (Signify-Resource, content-digest) must not leak
 * to third-party error trackers.
 *
 * NOTE: do NOT expose a `subscribe` parameter in any `ApiConfigOptions`
 * shape — doing so re-creates the auth-bypass leak vector identified by
 * the Security Hawk (Stage 4 critical #2 concern).
 *
 * @param waitForReady - resolves when Signify is ready to sign requests.
 *   Defaults to the production `waitForSignifyConnection`.
 * @param fetchImpl - the underlying fetch implementation; defaults to
 *   `window.fetch` at call time (NOT captured at construction time, so
 *   `attachInterceptor`'s `window.fetch` replacement is always honoured).
 */
export function makeCustomFetch(
  waitForReady: () => Promise<void> = waitForSignifyConnection,
  fetchImpl?: (url: string, init?: any) => Promise<any>
): (url: string, init?: any) => Promise<any> {
  // cd-0009: closure-scoped cancellation flag.
  // One `cancelled` flag per makeCustomFetch invocation — each call to this
  // factory produces an independent flag so multiple closures cannot interfere
  // with each other (test-isolation guarantee, and correctness in prod if ever
  // multiple configurations are live simultaneously).
  // When any request in this closure receives a 401, it sets `cancelled = true`
  // BEFORE invoking handleErrorResponse (which triggers killSession/clearSignify).
  // Setting the flag first closes the race where a sibling could complete its
  // `await waitForReady()` between the 401 return and the flag being raised.
  // NOTE: this closes the *signing* race (future requests refuse to sign with
  // about-to-be-stale credentials) but cannot recall bytes already on the wire —
  // a request that has already reached `await window.fetch(...)` carries a
  // signature derived from then-valid credentials and is allowed to complete.
  // The cryptographic property is intact; only the session-state-vs-server-state
  // alignment matters here, and that is bounded by the server's own session-revoke.
  let cancelled = false

  return async function customFetch(url: string, init?: any): Promise<any> {
    let path = ''
    try {
      path = new URL(url).pathname
    } catch {
      path = url
    }

    const needsAuthentication = !isUrlBlacklistedForSignedHeaders(path)

    if (needsAuthentication) {
      // cd-0009 pre-await check: refuse to sign if a sibling 401'd before we
      // entered the waitForReady gate. Only authenticated requests check this —
      // public (blacklisted) endpoints don't use session credentials so there is
      // no stale-signing risk for them.
      //
      // BUG-0039 / cd-0022 fix (Stage 8 S8-6): throw a CustomError with
      // title 'ERROR-401-CASCADE' instead of a plain Error so that
      // downstream Sentry capture (e.g. NotificationProcessor.start's outer
      // catch) preserves the 401 provenance. Pre-fix this throw was a plain
      // Error whose message lacked the ERROR-401 marker — telemetry lost the
      // 401 origin on tick 2+ of a polling cycle that started with a 401.
      if (cancelled) {
        throw new CustomError({
          title: 'ERROR-401-CASCADE',
          type: '',
          instance: '',
          code: 'UNAUTHORIZED',
          detail:
            'customFetch: session was cancelled by a concurrent 401; refusing to sign with stale credentials'
        })
      }

      // Row #23: waitForReady handles the connectivity gate internally.
      // The fast-path optimisation (skip wait if already CONNECTED) lives
      // inside the waitForReady implementation.
      await waitForReady()

      // cd-0009 post-await check: the gate may have resolved on the pre-kill
      // state if a sibling 401'd while we were waiting. Re-check here so we
      // don't sign with a stale signifyClient/userAidHab.
      if (cancelled) {
        throw new CustomError({
          title: 'ERROR-401-CASCADE',
          type: '',
          instance: '',
          code: 'UNAUTHORIZED',
          detail:
            'customFetch: session was cancelled by a concurrent 401; refusing to sign with stale credentials'
        })
      }
    }

    // Use injected fetchImpl if provided (for testing), otherwise use
    // window.fetch at call time so attachInterceptor's replacement is honoured.
    const fetch = fetchImpl ?? window.fetch
    const response = await fetch(url, init)

    if (response.ok) {
      return response
    }

    if (response.status === 401) {
      // cd-0009: set the flag BEFORE handleErrorResponse so that any sibling
      // request that completes its await-waitForReady between now and the
      // killSession/clearSignify mutation will see `cancelled = true` and throw
      // rather than sending signed bytes with the now-stale credentials.
      cancelled = true
    }

    return handleErrorResponse(response)
  }
}

const productionWaitForReady = waitForSignifyConnection

/**
 * Options for createApiConfig.
 *
 * IMPORTANT: both `fetchApi` and `waitForReady` must be provided together
 * or neither. Providing only one is almost always a bug:
 *   - fetchApi without waitForReady → real waitForSignifyConnection silently
 *     wired into the test-injected fetch, causing hangs.
 *   - waitForReady without fetchApi → stub waitForReady wired into production
 *     customFetch, which is usually correct but misleading.
 * createApiConfig throws if exactly one of the two is provided (Security Hawk
 * concern 1).
 *
 * DO NOT add a `subscribe` field here — exposing subscribe in this options
 * shape re-creates the auth-bypass leak vector identified by Security Hawk
 * concern #5 (same as the pre-refactor leak).
 */
export interface ApiConfigOptions {
  fetchApi?: (url: string, init?: any) => Promise<any>
  waitForReady?: () => Promise<void>
}

/**
 * Factory that returns a `ConfigurationParameters`-shaped params object
 * used to instantiate all 7 generated-SDK `Configuration` classes.
 *
 * Returns the params shape (NOT a typed `Configuration` instance) because
 * each generated package's `Configuration` class uses `private configuration`
 * which defeats structural typing. Callers construct per-package Configuration
 * instances: `new UserMgmtConfiguration(createApiConfig())`.
 *
 * See Stage 4 plan §1.2 and cd-0006 for rationale.
 *
 * NOTE: 401-handling via `handleErrorResponse` is NOT affected by the injected
 * `fetchApi`/`waitForReady` — `handleErrorResponse` is module-private and reads
 * `useStore.getState()` at call time. Tests that need to assert 401-side-effects
 * (killSession / clearSignify) must still mock the store directly.
 */
export function createApiConfig(opts: ApiConfigOptions = {}): {
  middleware: Middleware[]
  fetchApi: (url: string, init?: any) => Promise<any>
  basePath: string
} {
  const hasFetch = opts.fetchApi !== undefined
  const hasReady = opts.waitForReady !== undefined

  if (hasFetch !== hasReady) {
    throw new Error(
      'createApiConfig: fetchApi and waitForReady must be provided together. ' +
        'Providing only one is almost always a bug — both or neither.'
    )
  }

  const resolvedWaitForReady = opts.waitForReady ?? productionWaitForReady
  const resolvedFetch = opts.fetchApi ?? makeCustomFetch(resolvedWaitForReady)

  return {
    middleware: [authMiddleware],
    fetchApi: resolvedFetch,
    // PUBLIC_API_ENDPOINT is `string | undefined` (constants.ts §32 leaves it
    // un-cast, unlike sibling URL envs). Falling back to '' keeps the generated
    // SDK's `Configuration.basePath` strictly `string`; downstream fetch will
    // fail loudly if the env was actually unset, instead of silently passing
    // `undefined` to the SDK.
    basePath: constants.PUBLIC_API_ENDPOINT ?? ''
  }
}

// Each generated API package emits its own nominally-distinct `Configuration`
// class (the `private configuration` field defeats structural typing), so we
// instantiate one Configuration per package from a shared params object rather
// than casting through `any`.
//
// Intentional divergence from generated-SDK convention: createApiConfig returns
// a `ConfigurationParameters`-shaped params object, NOT a typed `Configuration`
// instance. This is necessary because no single `Configuration` type unifies
// across the 7 packages — each has `private configuration: ConfigurationParameters`.
// See Stage 4 plan §1.2 and cd-0006.
const apiConfigParams = createApiConfig()

const userMgmtConfig = new UserMgmtConfiguration(apiConfigParams)
const orgConfig = new OrgConfiguration(apiConfigParams)
const workflowConfig = new WorkflowConfiguration(apiConfigParams)
const l10nConfig = new L10nConfiguration(apiConfigParams)
const notificationConfig = new NotificationConfiguration(apiConfigParams)
const agentConfig = new AgentConfiguration(apiConfigParams)
const webmeetConfig = new WebmeetConfiguration(apiConfigParams)

export const userService = new UserControllerApi(userMgmtConfig)
export const emailService = new EmailControllerApi(userMgmtConfig)
export const oorTitleService = new OorTitleControllerApi(userMgmtConfig)
export const userRoleService = new UserRoleControllerApi(userMgmtConfig)
export const onboardService = new OnboardControllerApi(userMgmtConfig)

export const ecrPrerequisitesService = new ECRPrerequisitesControllerApi(
  workflowConfig
)
export const commonWorkflowService = new CommonWorkflowControllerApi(
  workflowConfig
)
export const oorPrerequisitesService = new OORPrerequisitesControllerApi(
  workflowConfig
)
export const utilWorkflowService = new UtilWorkflowControllerApi(workflowConfig)
export const vleiPrerequisitesService = new VLEIPrerequisitesControllerApi(
  workflowConfig
)
export const workflowInstanceService = new WorkflowInstanceControllerApi(
  workflowConfig
)

export const orgService = new OrgControllerApi(orgConfig)
export const orgResearchService = new OrgResearchControllerApi(orgConfig)
export const searchService = new SearchControllerApi(orgConfig)
export const serviceProviderService = new ServiceProviderControllerApi(
  orgConfig
)

export const l10nService = new L10nControllerApi(l10nConfig)
export const uiNotificationService = new UINotificationControllerApi(
  notificationConfig
)
export const agentMultisigService = new MultisigControllerApi(agentConfig)
export const agentAIDService = new AidControllerApi(agentConfig)
export const utilAgentService = new UtilAgentControllerApi(agentConfig)
export const webmeetService = new WebmeetControllerApi(webmeetConfig)
